// Safety Page
// Main page for Safety features (blocking, reporting, etc.)
// Following existing patterns from Dashboard.tsx and Messaging.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { safetyService } from '../../services/Safety';
import { BlockedUsersList, ReportUserModal, BlockUserModal } from '../../components/Safety';
import { BlockedUser, UserReport } from '../../types/safety';
import {
  SafetyContainer,
  SafetyHeader,
  SafetyHeaderTitle,
  SafetyHeaderActions,
  SafetyContent,
  SafetySection,
  SafetySectionTitle,
  SafetySectionDescription,
  SafetyCard,
  SafetyCardHeader,
  SafetyCardTitle,
  SafetyCardDescription,
  SafetyCardContent,
  SafetyCardActions,
  SafetyLoading,
  SafetyError,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from '../../styles/Safety';
import { theme } from '../../styles/theme';
import styled from 'styled-components';

const ActionButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: ${theme.colors.text.light};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.hover.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ReportItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.light};
  margin-bottom: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.sm};
  }
`;

const ReportInfo = styled.div`
  flex: 1;
`;

const ReportTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const ReportDetails = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

const ReportStatus = styled.span<{ $status: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'pending': return 'rgba(255, 193, 7, 0.1)';
      case 'reviewing': return 'rgba(23, 162, 184, 0.1)';
      case 'resolved': return 'rgba(40, 167, 69, 0.1)';
      case 'dismissed': return 'rgba(108, 117, 125, 0.1)';
      default: return 'rgba(108, 117, 125, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending': return '#ffc107';
      case 'reviewing': return '#17a2b8';
      case 'resolved': return '#28a745';
      case 'dismissed': return '#6c757d';
      default: return '#6c757d';
    }
  }};
  text-transform: capitalize;
`;

export const Safety: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  // Load data on mount
  useEffect(() => {
    loadSafetyData();
  }, []);

  const loadSafetyData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // The real endpoints. These have existed since the safety features
      // shipped; the page called the mock ones instead, so it showed invented
      // people no matter who you had actually blocked.
      const [blocked, reports] = await Promise.all([
        safetyService.getBlockedUsers(),
        safetyService.getUserReports()
      ]);
      
      setBlockedUsers(blocked);
      setUserReports(reports);
    } catch (err) {
      console.error('Error loading safety data:', err);
      setError('Failed to load safety data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockUser = async (reason?: string) => {
    if (!selectedUserId) return;
    
    try {
      const blockedUser = await safetyService.blockUser({
        userId: selectedUserId,
        reason
      });
      
      setBlockedUsers(prev => [...prev, blockedUser]);
      setShowBlockModal(false);
      setSelectedUserId(null);
      setSelectedUserName(null);
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user. Please try again.');
    }
  };

  const handleUnblockUser = async (userId: string) => {
    if (!confirm('Are you sure you want to unblock this user?')) {
      return;
    }
    
    try {
      await safetyService.unblockUser(userId);
      setBlockedUsers(prev => prev.filter(u => u.blockedUserId !== userId));
    } catch (err) {
      console.error('Error unblocking user:', err);
      alert('Failed to unblock user. Please try again.');
    }
  };

  const handleReportUser = async (reportType: string, reason: string, description?: string) => {
    if (!selectedUserId) return;
    
    try {
      const report = await safetyService.reportUser({
        reportedUserId: selectedUserId,
        reportType: reportType as any,
        reason,
        description
      });
      
      setUserReports(prev => [report, ...prev]);
      setShowReportModal(false);
      setSelectedUserId(null);
      setSelectedUserName(null);
      alert('Thank you for your report. We will review it shortly.');
    } catch (err) {
      console.error('Error reporting user:', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  const openBlockModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowBlockModal(true);
  };

  const openReportModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowReportModal(true);
  };

  if (isLoading) {
    return (
      <SafetyContainer>
        <SafetyContent>
          <SafetyLoading>Loading safety settings...</SafetyLoading>
        </SafetyContent>
      </SafetyContainer>
    );
  }

  return (
    <SafetyContainer>
      <SafetyContent>
        <SafetyHeader>
          {/* Reached from Settings, so it needs a way back there. Landing on a
              page with no exit is how people end up using the browser's back
              button to escape an app. */}
          <button
            type="button"
            onClick={() => navigate('/settings')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
              marginBottom: '0.75rem',
            }}
          >
            ← Settings
          </button>
          <SafetyHeaderTitle>Safety & Privacy</SafetyHeaderTitle>
          <SafetySectionDescription>
            Manage your safety settings, blocked users, and reports to ensure a safe and positive experience.
          </SafetySectionDescription>
        </SafetyHeader>

        {error && (
          <SafetyError>
            {error}
            <button 
              onClick={loadSafetyData}
              style={{
                marginLeft: '1rem',
                padding: '0.5rem 1rem',
                background: theme.colors.primary,
                color: theme.colors.text.light,
                border: 'none',
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </SafetyError>
        )}

        {/* Blocked Users Section */}
        <SafetySection>
          <SafetySectionTitle>Blocked Users</SafetySectionTitle>
          <SafetySectionDescription>
            Users you've blocked won't be able to contact you or see your profile. You can unblock them at any time.
          </SafetySectionDescription>
          <SafetyCard>
            <SafetyCardContent>
              <BlockedUsersList
                blockedUsers={blockedUsers}
                onUnblock={handleUnblockUser}
                isLoading={false}
              />
            </SafetyCardContent>
          </SafetyCard>
        </SafetySection>

        {/* Your Reports Section */}
        <SafetySection>
          <SafetySectionTitle>Your Reports</SafetySectionTitle>
          <SafetySectionDescription>
            View the status of reports you've submitted. Our team reviews all reports to keep the community safe.
          </SafetySectionDescription>
          <SafetyCard>
            <SafetyCardContent>
              {userReports.length === 0 ? (
                <EmptyState>
                  <EmptyStateIcon>📋</EmptyStateIcon>
                  <EmptyStateTitle>No Reports</EmptyStateTitle>
                  <EmptyStateDescription>
                    You haven't submitted any reports yet. If you encounter any issues, you can report users from their profile or conversation.
                  </EmptyStateDescription>
                </EmptyState>
              ) : (
                <div>
                  {userReports.map((report) => (
                    <ReportItem key={report.id}>
                      <ReportInfo>
                        <ReportTitle>
                          Reported: {report.reportedUser?.name || 'Unknown User'}
                        </ReportTitle>
                        <ReportDetails>
                          <div>Type: {report.reportType.replace('_', ' ')}</div>
                          <div>Reason: {report.reason}</div>
                          <div>Submitted: {new Date(report.createdAt).toLocaleDateString()}</div>
                        </ReportDetails>
                      </ReportInfo>
                      <ReportStatus $status={report.status}>
                        {report.status}
                      </ReportStatus>
                    </ReportItem>
                  ))}
                </div>
              )}
            </SafetyCardContent>
          </SafetyCard>
        </SafetySection>

        {/* Safety Tips Section */}
        <SafetySection>
          <SafetySectionTitle>Safety Tips</SafetySectionTitle>
          <SafetyCard>
            <SafetyCardContent>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.md
              }}>
                <li style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <span style={{ fontSize: '1.5rem' }}>💬</span>
                  <div>
                    <strong>Keep conversations respectful</strong>
                    <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
                      Report any harassment or inappropriate behavior immediately.
                    </div>
                  </div>
                </li>
                <li style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <span style={{ fontSize: '1.5rem' }}>🔒</span>
                  <div>
                    <strong>Block users when needed</strong>
                    <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
                      You can block users at any time from their profile or conversation.
                    </div>
                  </div>
                </li>
                <li style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <span style={{ fontSize: '1.5rem' }}>📞</span>
                  <div>
                    <strong>Meet in public places</strong>
                    <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
                      Always meet in public places for your first few dates.
                    </div>
                  </div>
                </li>
                <li style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <span style={{ fontSize: '1.5rem' }}>🚨</span>
                  <div>
                    <strong>Trust your instincts</strong>
                    <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
                      If something feels off, report it. We're here to help keep you safe.
                    </div>
                  </div>
                </li>
              </ul>
            </SafetyCardContent>
          </SafetyCard>
        </SafetySection>

        {/* Modals */}
        <ReportUserModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setSelectedUserId(null);
            setSelectedUserName(null);
          }}
          onReport={handleReportUser}
          reportedUserName={selectedUserName || undefined}
        />

        <BlockUserModal
          isOpen={showBlockModal}
          onClose={() => {
            setShowBlockModal(false);
            setSelectedUserId(null);
            setSelectedUserName(null);
          }}
          onBlock={handleBlockUser}
          userName={selectedUserName || undefined}
        />
      </SafetyContent>
    </SafetyContainer>
  );
};

